/**
 * SectionBoundary: a boundary that contains a crash to ONE panel.
 *
 * `ErrorBoundaryNext` is the last resort: it replaces the whole app with a
 * recovery screen. That is the right answer for a broken shell and the wrong
 * one for a single exhibit. A malformed attachment that throws while its
 * section renders should cost the reader that section, not the verdict, the
 * authentication passport, and everything else they were reading.
 *
 * So every routed view and every report section renders inside one of these.
 * The failed panel becomes a dashed well naming what broke, with a Retry that
 * remounts just that subtree (the message is still parsed; a re-render is
 * often enough after a transient failure such as a lazy chunk that 404'd).
 *
 * Deliberately NOT a toast: the reader must be able to see WHICH part of the
 * page is missing, in place, or they will read an incomplete report as a
 * complete one.
 */
import { Component, Fragment, type ErrorInfo, type ReactNode } from "react";
import { STORAGE_KEYS } from "../utils/storageKeys";
import { isChunkLoadError } from "./chunkError";
import type { Locale } from "./i18n";

interface SectionCopy {
  /** Mono eyebrow above the message. */
  label: string;
  /** Body line; `{name}` is replaced with the panel's name. */
  body: string;
  /** Body line when the caller gave no name. */
  bodyUnnamed: string;
  retry: string;
}

const COPY: Record<Locale, SectionCopy> = {
  en: {
    label: "Section unavailable",
    body: "{name} could not be rendered. The rest of this report is unaffected.",
    bodyUnnamed:
      "This section could not be rendered. The rest of this report is unaffected.",
    retry: "Try again",
  },
  de: {
    label: "Abschnitt nicht verfügbar",
    body: "{name} konnte nicht dargestellt werden. Der Rest dieses Berichts ist davon unberührt.",
    bodyUnnamed:
      "Dieser Abschnitt konnte nicht dargestellt werden. Der Rest dieses Berichts ist davon unberührt.",
    retry: "Erneut versuchen",
  },
};

/**
 * Same resolution as ErrorBoundaryNext: a class component cannot call useT,
 * and the boundary has to keep working when the tree around it is broken, so
 * it reads the persisted preference directly.
 */
function resolveLocale(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.locale);
    if (v === "en" || v === "de") return v;
  } catch {
    /* storage unavailable (private mode) */
  }
  const nav = (
    typeof navigator !== "undefined" ? (navigator.language ?? "") : ""
  ).toLowerCase();
  if (nav.startsWith("de")) return "de";
  return "en";
}

interface Props {
  children: ReactNode;
  /** Human name of the panel, e.g. "Attachments". Shown in the notice. */
  name?: string;
  /**
   * Identity of the content inside. When it changes the boundary clears
   * itself, so navigating to another route or loading another message never
   * leaves a stale notice from the previous one on screen.
   *
   * REQUIRED, deliberately. With it optional, a caller who forgot it got a
   * boundary that could never clear: the notice would outlive the content it
   * belonged to and mislabel whatever replaced it. Making the compiler ask is
   * cheaper than the review that catches it.
   */
  resetKey: string | number;
  /**
   * This boundary stands in for a WHOLE routed view, not a panel inside one.
   * Every view renders `<main id="main">`; if one throws, the notice has to
   * supply that landmark itself, or the skip link and the router's
   * focus-the-main behaviour both point at nothing.
   */
  asMain?: boolean;
}

interface State {
  error: Error | null;
  /**
   * The resetKey seen on the last render. Tracked on EVERY error-free render,
   * which is what makes it the key the failing content had at the moment it
   * threw: `getDerivedStateFromError` cannot see props, so the key has to
   * already be in state by then.
   */
  seenKey: string | number | undefined;
  /** Bumped by Retry to force a fresh mount of the children. */
  attempt: number;
}

export class SectionBoundary extends Component<Props, State> {
  state: State = { error: null, seenKey: undefined, attempt: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (!state.error) {
      // Error-free render: remember the key this content is being shown under.
      return state.seenKey === props.resetKey ? null : { seenKey: props.resetKey };
    }
    // Holding an error. Clear it only once the key has MOVED, which means the
    // caller swapped in different content (a new route, a new message).
    //
    // The obvious-looking alternative, stamping the key when the error is
    // caught, does not work: React runs getDerivedStateFromProps with the
    // post-catch state before it renders the fallback, `seenKey` would still
    // be its initial undefined, and the very first comparison would clear a
    // real error. The boundary then re-rendered the throwing child forever,
    // or escalated to the whole-app screen. That is exactly what happened
    // here, and the tests missed it because none of them passed a resetKey.
    return state.seenKey === props.resetKey
      ? null
      : { error: null, seenKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[mime-smith] section "${this.props.name ?? "(unnamed)"}" failed to render`,
      error,
      info.componentStack,
    );
  }

  private retry = () => {
    this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));
  };

  render(): ReactNode {
    // A failed dynamic import is NOT a section-level failure, and this
    // boundary must not pretend it can fix one. React caches a `lazy()`
    // loader's promise INCLUDING its rejection, so Retry re-throws the same
    // rejection forever; and the usual cause is a stale deploy, where the open
    // tab asks for chunk hashes the server no longer has, so no amount of
    // re-rendering helps. The cure is one hard reload against the new
    // index.html, which `ErrorBoundaryNext` implements (guarded against a
    // reload loop). So these rethrow, past this boundary, to the one that can
    // actually recover. WHICH failures those are is decided in ONE place,
    // `./chunkError`, because this rethrow is only safe while that boundary
    // agrees with this one: a wording only this side recognises costs the
    // reader both the section notice and the reload.
    if (this.state.error && isChunkLoadError(this.state.error)) {
      throw this.state.error;
    }
    if (!this.state.error) {
      // A KEYED FRAGMENT, not a wrapper element: `attempt` forces a fresh
      // mount on Retry rather than a re-render of the subtree that just threw,
      // and adds no DOM node. A wrapper div here would sit between .nx-app and
      // the view's own <main>, breaking the shell's column layout.
      return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;
    }
    const c = COPY[resolveLocale()];
    const body = this.props.name
      ? c.body.replace("{name}", this.props.name)
      : c.bodyUnnamed;
    const notice = (
      <div className="nx-secfail" role="status">
        <span className="nx-secfail__label">{c.label}</span>
        <p className="nx-secfail__body">{body}</p>
        <button type="button" className="nx-btn nx-secfail__retry" onClick={this.retry}>
          {c.retry}
        </button>
      </div>
    );
    if (this.props.asMain) {
      return (
        <main className="nx-doc" id="main">
          {notice}
        </main>
      );
    }
    return notice;
  }
}
